import { useState, useRef, useEffect, useMemo } from "react";
// import { useGetTitle } from "../../../hooks/useGetTitle";
import { useParams } from "react-router-dom";
// import { toast } from "react-toastify";

import { 
  Alert,
  Box, Button, 
  Chip,
  Collapse,
  Divider,
  FormControl, FormControlLabel,
  IconButton, InputAdornment,
  MenuItem, 
  OutlinedInput,
  Paper,
  Select, Switch,
  Stack,
  Typography, TextField,
  useTheme 
} from "@mui/material";

import styled from "styled-components";

import SearchIcon from "@mui/icons-material/Search"; // MUI의 검색 아이콘
import DescriptionIcon from "@mui/icons-material/Description"; // MUI 문서 아이콘
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import VerifiedIcon from "@mui/icons-material/Verified";

import dayjs from "dayjs";

import { 
  useGetTableData, useGetTableDataInQueryString,
  useCreateTableData, 
  usePutTableData, 
  useDeleteTableData 
} from "../../../components/Table/hooks";

import { 
  strategyResultColumns, strategyResultColumns_admin,
  strategyEmpSearchColumns,
  ImageRenderState, 
} from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import { ImageUploader } from "../../../components/Table/ImageUploader";    
import Buttons from "../../../../common/Buttons";
import { EmptyState } from "../../../components/common/EmptyState";
import { 
  BreadcrumbsTable,
  ConfirmDialog, ConfirmDialogSingle,
  SnackbarAlert,
  formatDateWithDayjs, formatDateToDayjs, 
} from "../../../components/common/HelperFunc";
import * as message from "../../../constants/messages";
import { FONT_PROPS, COLORS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 600px) {
    padding: 12px;
    overflow: auto;
  }
`;

//////////////////////////////////
// 개선대책 실행결과 등록 컴포넌트
//////////////////////////////////
const PARENT_TITLE = "대책관리";
const CHILD_TITLE = "개선대책 실행결과 등록";
const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const isDeletedStrategyRow = (row) => (
  row?.register_cause_status === 1 ||
  row?.process_status === 1 ||
  row?.register_asset_status === 1
);
const isCompletedImprove = (row) => toNumber(row?.improve_status) >= 20;
const isOverdueImprove = (row) => (
  row?.expected_date &&
  !isCompletedImprove(row) &&
  !isDeletedStrategyRow(row) &&
  dayjs(row.expected_date).isBefore(dayjs(), "day")
);
const needsAfterPhoto = (row) => !isCompletedImprove(row) && !isDeletedStrategyRow(row) && !row?.improve_after;
const FILTER_STATUS = Object.freeze({
  ALL: "all",
  REGISTERED: "registered",
  DELETED: "deleted",
});

export function StrategyResult() {
  const theme = useTheme();
  const { group_code, project_code } = useParams();

  const getSummaryItems = () => [
    {
      label: "처리 대상",
      value: resultSummary.activeCount,
      icon: <BuildCircleIcon />,
      tone: "#2563eb",
    },
    {
      label: "완료 대기",
      value: resultSummary.waitingCount,
      icon: <AssignmentTurnedInIcon />,
      tone: "#f97316",
    },
    {
      label: "완료",
      value: resultSummary.completedCount,
      icon: <VerifiedIcon />,
      tone: "#16a34a",
    },
    {
      label: "기한 초과",
      value: resultSummary.overdueCount,
      icon: <EventBusyIcon />,
      tone: "#dc2626",
    },
    {
      label: "사진 필요",
      value: resultSummary.photoMissingCount,
      icon: <ImageSearchIcon />,
      tone: "#7c3aed",
    },
  ];

  const renderSummaryPanel = () => {
    const summaryItems = getSummaryItems();

    return (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 2,
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={resultSummary.overdueCount > 0 ? "error" : "success"}
              label={resultSummary.overdueCount > 0 ? "기한 초과 확인" : "개선 실행 관리"}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary" }}>
              개선대책 완료 처리
            </Typography>
          </Stack>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "text.secondary" }}>
            개선 후 사진과 완료일을 입력한 뒤 저장하면 개선 완료 상태로 전환됩니다.
          </Typography>
        </Box>
        <Box sx={{ width: { xs: "100%", md: 220 } }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
            <Typography variant="body2" sx={{ fontWeight: 900 }}>완료율</Typography>
            <Typography variant="body2" sx={{ fontWeight: 900 }}>{resultSummary.completionRate}%</Typography>
          </Stack>
          <Box sx={{ width: "100%", height: 9, borderRadius: 99, bgcolor: "#e2e8f0", overflow: "hidden" }}>
            <Box sx={{ width: `${resultSummary.completionRate}%`, height: "100%", bgcolor: "#16a34a" }} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(5, minmax(0, 1fr))",
          },
        }}
      >
        {summaryItems.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              minWidth: 0,
              p: 2,
              borderRight: {
                md: index === summaryItems.length - 1 ? "none" : `1px solid ${theme.palette.divider}`,
              },
              borderBottom: {
                xs: index < 4 ? `1px solid ${theme.palette.divider}` : "none",
                md: "none",
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  display: "grid",
                  placeItems: "center",
                  color: item.tone,
                  bgcolor: `${item.tone}14`,
                  flex: "0 0 auto",
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 700 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ fontSize: 24, color: "text.primary", fontWeight: 850, lineHeight: 1.1 }}>
                  {item.value}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Box>

      <Divider />

      <Alert severity={resultSummary.activeCount > 0 ? "info" : "warning"} sx={{ borderRadius: 0 }}>
        {resultSummary.activeCount > 0
          ? "완료 대기 항목은 개선 후 사진과 완료일을 모두 입력해야 저장할 수 있습니다. 삭제 항목은 완료 처리에서 제외됩니다."
          : "표시할 개선대책 실행 대상이 없습니다. 개선대책 수립 화면에서 대책 등록 후 이 화면에서 완료 처리할 수 있습니다."}
      </Alert>
    </Paper>
    );
  };

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회에 필요한 조건을 상태로 관리합니다. (평가연도, 평가월, 설비명)
  const [searchStatus, setSearchStatus] = useState({});
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = { 
    params: { // 쿼리스트링 만들때 params를 사용하는게 규칙입니다.
      year: searchStatus.year,
      month: searchStatus.month,
      machine_id: searchStatus.machine_id,
      filter: searchStatus.filter,
    },
  }

  // 안전보건 법규 조회 요청
  const getSafeLawData = useGetTableData(
    "request",
    "/getSafeLaw", 
    group_code,
    project_code
  );

  // 유해위험요인등록 현황 조회 요청
  const getRegisterStatusData = useGetTableData(
    "request",
    "/getRegisterStatus", 
    group_code,
    project_code
  );

  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getStrategyResult",
    group_code,
    project_code,
    qeuryString,
  );
  // 데이터 수정 요청
  const putData = usePutTableData(
    "request",
    "/putStrategy",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteStrategy",
    group_code,
    project_code
  );

  // 필터링 목록 조회 요청 (평가년도별, 평가년도의 최신 평가 월을 가져옵니다.)
  const getFilterListData = useGetTableData(
    "request",
    "/getStrategyResultFilterList",
    group_code,
    project_code,
  );

  // 개선전.후 이미지 생성/수정 요청
  const postImproveImageData = useCreateTableData(
    "request",
    "/postStrategyResultImage",
    group_code,
    project_code
  );

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 메시지 박스의 알람 메시지
  const [messageAlarm, setMessageAlarm] = useState({
    isOpen: false,
    title: '',
    message: '',
  });
  const openAlarmMessage = (title='', message='',) => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: true,
      title: title,
      message: message,
    });
  };
  const closeAlarmMessage = () => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: false,
      title: '',
      message: '',
    });
  };

  // 개선전,후 사진 모달 상태 관리
  const [showImproveImage, setShowImproveImage] = useState({ isOpen: false, state: ImageRenderState.NONE, rowData: null });
  const openShowImproveImage = (state, rowData) => setShowImproveImage({ isOpen: true, state: state, rowData: rowData });
  const closeShowImproveImage = () => setShowImproveImage({ isOpen: false, state: ImageRenderState.NONE, rowData: null });

  // 담당자 모달 창 상태 관리
  const [searchEmployee, setSearchEmployee] = useState({ isOpen: false, rowData: null });
  const openSearchEmployee = (rowData) => setSearchEmployee({ isOpen: true, rowData: rowData });
  const closeSearchEmployee = () => setSearchEmployee({ isOpen: false, rowData: null });

  // 필터 초기값 설정
  useEffect(() => {
    // 데이터가 로드되지 않았거나 비어 있는 경우 처리 중단
    if(!getFilterListData.data || getFilterListData.data.length === 0 ) {
      return;
    }
    if(!getRegisterStatusData.data || getRegisterStatusData.data.length === 0) {
      return;
    }
    
    // 최신 평가연도 데이터 가져오기
    let latestYearData = null;
    for(let i = getFilterListData.data.length - 1; i >= 0; i--) {
      if(getFilterListData.data[i].create_year && getFilterListData.data[i].latest_month) {
        latestYearData = getFilterListData.data[i];
        break;
      }
    }
    if(!latestYearData) {
      return; // 최신 평가연도 데이터가 없으면 처리 중단
    }

    const data = 
    {
      year: latestYearData.create_year,
      month: Number(latestYearData.latest_month),
      machine_id: FILTER_STATUS.ALL, // 모든 설비를 조회하기 위해 'all'로 설정합니다.
      filter: FILTER_STATUS.ALL,
    };
    
    setSearchStatus(data);
  }, [getFilterListData.data, getRegisterStatusData.data]); 

  // 데이터 가공
  const [tempGetData, setTempGetData] = useState(null);
  const handleSetTempGetData = (data) => setTempGetData(data);
  useEffect(() => {
    const tempData = [...getData.data];
    for(let i = 0; i < tempData.length; ++i) {
      // 데이터에 인덱스 추가(행 편집 용도로 사용)
      tempData[i].__index = i;
      // 담당자 설정 모달 활성화 여부를 나타내는 상태 변수 (관리자 모드시 사용)
      tempData[i].__hasSearchEmployee = false;
      // 개선 전,후 사진 추가, 수정, 확대 상태 변수
      tempData[i].__imageStatus = ImageRenderState.NONE;
      // 개선전 사진 url 추가 (행 수정 후 이미지 업로드를 위해 필요)
      tempData[i].__improveBeforeUrl = tempData[i].improve_before;
      // 개선후 사진 url 추가 (행 수정 후 이미지 업로드를 위해 필요)
      tempData[i].__improveAfterUrl = tempData[i].improve_after;
      // 개선 예정일 날짜 추가 (관리자 모드 시 날짜 포맷 변경을 위해 필요)
      tempData[i].__expectedDate = formatDateToDayjs(tempData[i].expected_date);
      // 개선 완료일 날짜 추가 (관련된 날짜 포맷 변경을 위해 필요)
      tempData[i].__compDate = tempData[i].comp_date;
      // 배경색 설정
      const isDeleted = tempData[i].register_cause_status === 1
        || tempData[i].process_status === 1
        || tempData[i].register_asset_status === 1;
      tempData[i].__bgColor = isDeleted 
        ? {
            backgroundColor: "#ffebeb", // 연한 빨간색 배경
            backgroundImage:
              "repeating-linear-gradient(45deg, #ffcccc 0, #ffcccc 2px, transparent 2px, transparent 6px)",
            backgroundSize: "8px 8px",
          } 
          : theme.palette.background.paper;

          tempData[i].__expectedDate = formatDateToDayjs(tempData[i].expected_date);

      // 개선 완료일 날짜 포맷 변경
      tempData[i].comp_date = formatDateToDayjs(tempData[i].comp_date);
    }
    handleSetTempGetData(tempData);
  },[getData.data, theme.palette.background.paper]);

  const resultSummary = useMemo(() => {
    const rows = tempGetData || [];
    const activeRows = rows.filter((row) => !isDeletedStrategyRow(row));
    const completedCount = activeRows.filter(isCompletedImprove).length;
    const waitingCount = activeRows.length - completedCount;
    const overdueCount = activeRows.filter(isOverdueImprove).length;
    const photoMissingCount = activeRows.filter(needsAfterPhoto).length;
    const deletedCount = rows.length - activeRows.length;
    const completionRate = activeRows.length
      ? Math.round((completedCount / activeRows.length) * 100)
      : 0;

    return {
      total: rows.length,
      activeCount: activeRows.length,
      waitingCount,
      completedCount,
      overdueCount,
      photoMissingCount,
      deletedCount,
      completionRate,
    };
  }, [tempGetData]);
  const isProcessing = Boolean(putData.isPending || putData.isLoading || deleteData.isPending || deleteData.isLoading || postImproveImageData.isPending || postImproveImageData.isLoading);

  // 평가연도 목록 (중복 제거를 위한 작업)
  const yearList = Array.from(
    new Set(getFilterListData.data.map(item => item.create_year))
  );
  
  // 설비명 목록 (중복 제거를 위한 작업)
  const machineIdList = Array.from(
    new Map(
      getRegisterStatusData.data.map((item) => [
        item.machine_id, 
        { 
          machine_id: item.machine_id, 
          machine_name: item.machine_name 
        }
      ])
    ).values()
  );
  machineIdList.unshift({ machine_id: FILTER_STATUS.ALL, machine_name: '전체' }); // 전체 추가

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const [showAdminMode, setShowAdminMode] = useState(false); // 관리자 모드 활성화 여부
  const filters = [
    { label: "전체", value: FILTER_STATUS.ALL },
    { label: "유지 항목", value: FILTER_STATUS.REGISTERED },
    { label: "삭제 항목", value: FILTER_STATUS.DELETED },
  ];
  const customTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display={"flex"} alignItems="center" gap={4} marginLeft={'16px'}>
        {/* 평가연도 카테고리 */}
        <FormControl size="small">
          <Box sx={{display: "flex",alignItems: "center",gap: "16px",}}>
            <Typography sx={{fontWeight: FONT_PROPS.SEMIBOLD,fontSize: 15,color: theme.text,}}>
              평가연도
            </Typography>
            <Select
              value={searchStatus.year || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  year: e.target.value,
                });
              }}
              sx={{ width: "100px",fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
            >
              {/* 필터 목록 */}
              {yearList.reverse().map((year, index) => (
                <MenuItem
                  key={index}
                  value={year}
                  sx={{ fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
                >
                  {year}년
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 평가월 카테고리 */}
        <FormControl size="small">
          <Box sx={{display: "flex",alignItems: "center",gap: "16px",}}>
            <Typography sx={{fontWeight: FONT_PROPS.SEMIBOLD,fontSize: 15,color: theme.text,}}>
              평가월
            </Typography>
            <Select
              value={searchStatus.month || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  month: e.target.value,
                });
              }}
              sx={{ width: "80px",fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
            >
              {/* 필터 목록 */}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].reverse().map((month, index) => (
                <MenuItem
                  key={index}
                  value={month}
                  sx={{fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
                >
                  {month}월
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 설비명 카테고리 */}
        <FormControl size="small">
          <Box sx={{display: "flex",alignItems: "center",gap: "16px",}}>
            <Typography sx={{fontWeight: FONT_PROPS.SEMIBOLD,fontSize: 15,color: theme.text,}}>
              설비명
            </Typography>
            <Select
              value={searchStatus.machine_id || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  machine_id: e.target.value,
                });
              }}
              sx={{ minWidth: "120px",fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
            >
              {/* 필터 목록 */}
              {machineIdList.map((data, index) => (
                <MenuItem
                  key={index}
                  value={data.machine_id}
                  sx={{fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
                >
                  {data.machine_name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 관련 법규 조회 버튼 */}
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={() => {
            //console.log("감소대책 수립 가이드 클릭!!");
            const data = {...getSafeLawData.data[1]};
            window.open(data.law_url, '_blank', 'noopener,noreferrer');
          }}
          sx={{fontWeight: FONT_PROPS.MEDIUM,fontSize: '15px',padding: "8px 16px 8px 16px",mr: "8px",}}
        >
          관련 법규 조회
        </Button>
      </Box>
      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
        
          <Collapse in={showSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                input: {
                  endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setGlobalFilter('');
                        table.setGlobalFilter('');
                        setShowSearch(false); // 닫기
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                  ),
                }
              }}
            />
          </Collapse>
        </Box>

        {/* 필터 */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {filters.map((filter) => {
            const isActive = searchStatus.filter === filter.value;
            return (
              <Box
                key={filter.value}
                onClick={() => setSearchStatus({ ...searchStatus, filter: filter.value })}
                sx={{
                  display: "flex",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: "1.375rem",
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all 0.2s ease",
                  backgroundColor: isActive
                    ? 'rgb(var(--mui-palette-primary-mainChannel) / 0.25)'
                    : "transparent",
                  color: isActive
                    ? '#7367F0'
                    : theme.palette.text.secondary,
                  "&:hover": {
                    backgroundColor: isActive
                      ? theme.palette.primary
                      : theme.palette.action.hover,
                    color: '#7367F0'
                  },
                  "&:active": {
                    transform: "scale(0.97)",
                  },
                }}
              >
                <Typography variant="body2" component="div" color="inherit"
                  sx={{ fontWeight: FONT_PROPS.MEDIUM, fontSize: 14, px: 3, py: 2, }}>
                  {filter.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* 삭제버튼 */}
        {showAdminMode && 
          <Buttons.Delete 
            onClick={openDeleteModal} // 행 삭제 로직 실행
            disabled={table.getSelectedRowModel().rows.length === 0}
            sx={{ padding: '12px 16px' }}
          >
            데이터 삭제
          </Buttons.Delete>
        }
        {/* 관리자 모드 */}
        <FormControl component="fieldset">
          <FormControlLabel
            value="top"
            control={
            <Switch color="primary" 
              checked={showAdminMode}
              onChange={(event) => {
                setShowAdminMode(event.target.checked);
                clearSelection();
              }}
            />
            }
            label="관리자모드"
            labelPlacement="end" // bottom,end,start,top
            sx={{marginLeft : '4px'}}
          />
        </FormControl>
      </Box>
    </Box>
  );

  // 행 편집 작업 (개선 완료일, 개선전 사진, 개선후 사진)
  const changeRowData = ({ values, row, table }) => {
    //console.log("✅ 편집 호출 완료!", values);
    //console.log("✅ 편집 호출 완료!", row.original);

    // 개선전.후 사진 등록 모달 열기
    if(values.__imageStatus !== ImageRenderState.NONE) {
      openShowImproveImage(values.__imageStatus, {...values}); // 개선 전,후 사진 등록 모달 열기
      return;
    }

    // 담당자 검색 모달 표시
    if(values.__hasSearchEmployee) {
      openSearchEmployee({...values}); // 위험성 감소대책 일정관리의 행 데이터 저장
      return;
    }

    // 편집된 데이터 변경
    const findData = tempGetData.find(item => {
      return item.__index === row.original.__index;
    });
    Object.keys(values).forEach((key) => {
      if (findData[key] !== values[key]) {
        findData[key] = values[key];
      }
    });

    // TempGetData 변경
    const tempData = tempGetData.map(item => {
      return item.__index === row.original.__index ? findData : item;
    });
    setTempGetData(tempData);
  };
  // 감소대책 실행 결과 현황 테이블의 전체 행 데이터 편집 완료 액션
  const completeEditAllConfirm = !showAdminMode ? (
    <Buttons.Custom
      startIcon={<CheckIcon />}
      disabled={isProcessing}
      onClick={(event) =>{
        event.stopPropagation(); // 이벤트 전파 중단
        //
        const findDatas = tempGetData.filter((item) => {
            // 1. comp_date 속성의 데이터가 있는지 확인
            // 2. improve_status 속성의 데이터가 없거나, 있더라도 개선완료 전인지 확인
            // 3. register_cause_status 속성의 데이터가 삭제인지 확인
            const hasDatas = item.comp_date && item.improve_after;
            // 20: 개선완료, 1: 삭제
            const isNotExpected = !item.improve_status || item.improve_status < 20;
            return hasDatas && isNotExpected && !isDeletedStrategyRow(item);
        });
          
        if(findDatas.length <= 0) {
          openAlarmMessage(
            '안내',
            '전체 저장할 항목이 없습니다.'
          );
          return;
        }
          
        updateEditData(findDatas);
      }}
      sx={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}
    >
      전체 저장
    </Buttons.Custom>
  ) : (
    <Buttons.Custom
      startIcon={<CheckIcon />}
      disabled={isProcessing}
      onClick={(event) =>{
        event.stopPropagation(); // 이벤트 전파 중단
        //
        const findDatas = tempGetData.filter((item) => {
          const expectedDate = dayjs(item.__expectedDate).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환
          const compDate = dayjs(item.comp_date).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환
          const hasValidData = !item.improve_before || !item.improve_plan || !item.improve_after
            || !item.improve_after_risk || !expectedDate || !compDate || !item.emp_id;
          return !hasValidData && !isDeletedStrategyRow(item);
        });
          
        if(findDatas.length <= 0) {
          openAlarmMessage(
            '안내',
            '전체 저장할 항목이 없습니다.'
          );
          return;
        }

        updateEditDataByAdmin(findDatas);
      }}
      sx={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}
    >
      전체 수정
    </Buttons.Custom>
  );
  // 테이블의 행(row) 저장 액션
  const completeEditConfirm = (rowData) => {
    // 개선완료거나 삭제된 항목은 저장 불가
    const isDisabled = rowData.improve_status >= 20 
      || rowData.register_cause_status === 1
      || rowData.process_status === 1
      || rowData.register_asset_status === 1;
    return(
      <>
        {!showAdminMode ? (
          <Buttons.Custom
            //startIcon={<CheckIcon />}
            disabled={isDisabled || isProcessing}
            onClick={(event) =>{
              event.stopPropagation(); // 이벤트 전파 중단
              if(checkData(rowData)) {
                openAlarmMessage(
                  '안내',
                  '개선후 사진, 개선완료일을 설정해주세요.'
                );
                return;
              }
                
              updateEditData(rowData);
            }}
            sx={{ padding: '8px 12px' }}
          >
            저장
          </Buttons.Custom>
        ) : (
          <Buttons.Custom
            //startIcon={<CheckIcon />}
            disabled={isDeletedStrategyRow(rowData) || isProcessing}
            onClick={(event) =>{
              event.stopPropagation(); // 이벤트 전파 중단
              if(checkDataByAdmin(rowData)) {
                openAlarmMessage(
                  '안내',
                  '위험성 개선대책 수립, 개선후 위험성, 개선예정일, 개선전 사진, 개선후 사진 개선완료일, 담당자를 설정해주세요.'
                );
                return;
              }

              updateEditDataByAdmin(rowData);
            }}
            sx={{ padding: '8px 12px' }}
          >
            수정
          </Buttons.Custom>
        )}
      </>
    );
  };
  // DB 업데이트 (수정 데이터)
  const updateEditData = async (rowDatas) => {
    try {
      // 배열이 아닌 경우 배열로 변환
      const tempRowDatas = Array.isArray(rowDatas)? rowDatas: [rowDatas]; 

      // 이미지를 업로드 용도의 formData로 변경
      const formatImageWithFormData = (value) => {
        const keys = [ "improve_after_image" ]; // 'keys'는 백엔드에서 이미지를 찾는 key값으로 사용함.
        const formData = new FormData();
        formData.append(keys[0], value[keys[0]], `StrategyResult_after_${value.improve_id}`); // key, data, 이미지 파일명
        return formData;
      }
      
      const updateData = await Promise.all( // 비동기 병렬 처리
        tempRowDatas.map(async (item) => {
          // 이미지 로컬 저장소에 저장
          const formData = formatImageWithFormData(item);
          const urls = await postImproveImageData.mutateAsync(formData);
          let url = null;
          const hasData = urls.data.url.find(item => item.key === "improve_after_image");
          if(hasData) { url = hasData.url; }

          // 데이터 가공
          return {
            improve_id: item.improve_id,
            comp_date: formatDateWithDayjs(item.comp_date),
            improve_after: url,
            improve_status: 20, // 20: 개선완료
          };
        })
      );

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync({ updateData });
      if(result.status === 200) {
        openDataAlert(`${updateData.length}건의 개선대책 완료 처리가 저장되었습니다.`);
        await getData.refetch?.();
        clearSelection();
      }
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  }
  // DB 업데이트 (수정 데이터) - 관리자 모드
  const updateEditDataByAdmin = async (rowDatas) => {
    try {
      // 배열이 아닌 경우 배열로 변환
      const tempRowDatas = Array.isArray(rowDatas)? rowDatas: [rowDatas]; 
      
      // 이미지를 업로드 용도의 formData로 변경
      const formatImageWithFormData = (value) => {
        const formData = new FormData();
        const keys = [ 
          "improve_before_image", "beforeFilename", 
          "improve_after_image", "afterFilename"
        ]; // 'keys'는 백엔드에서 이미지를 찾는 key값으로 사용함.

        // 새 이미지 파일이 있을 때
        if(value[keys[0]] instanceof File) {
          formData.append(keys[0], value[keys[0]], `StrategyResult_before_${value.eval_id}`); // 새 이미지 파일
          if(value.__improveBeforeUrl) {
            const filename = value.__improveBeforeUrl.split('/').pop();
            formData.append(keys[1], filename); // 기존 이미지 파일 이름
          }
        }
        if(value[keys[2]] instanceof File) {
          formData.append(keys[2], value[keys[2]], `StrategyResult_after_${value.eval_id}`); // 새 이미지 파일
          if(value.__improveAfterUrl) {
            const filename = value.__improveAfterUrl.split('/').pop();
            formData.append(keys[3], filename); // 기존 이미지 파일 이름
          }
        }
        return formData;
      }

      const updateData = await Promise.all( // 비동기 병렬 처리
        tempRowDatas.map(async (item) => {
          // 이미지 로컬 저장소에 저장
          const formData = formatImageWithFormData(item);
          let url1 = item.__improveBeforeUrl;
          let url2 = item.__improveAfterUrl;
          if([...formData.entries()].length > 0) {
            const urls = await postImproveImageData.mutateAsync(formData);
            const hasData1 = urls.data.url.find(item => item.key === 'improve_before_image');
            const hasData2 = urls.data.url.find(item => item.key === 'improve_after_image');
            if(hasData1) { url1 = hasData1.url; }
            if(hasData2) { url2 = hasData2.url; }
          }

          // 개선완료일이 변경되었는지 확인 후 반영
          const __compDate = formatDateWithDayjs(item.__compDate); // original
          const compDate = formatDateWithDayjs(item.comp_date); // edited
          const isCompDateSame = dayjs(__compDate).isSame(dayjs(compDate), 'day');
          let updateDate = item.improve_update_date;
          if(!isCompDateSame) {
            updateDate = compDate;
          }

          // 개선예정일이 변경되었는지 확인 후 반영
          const __expectedDate = formatDateWithDayjs(item.__expectedDate); // edit data
          const expectedDate = formatDateWithDayjs(item.expected_date); // original
          const isExpectedDateSame = dayjs(__expectedDate).isSame(dayjs(expectedDate), 'day');
          let createDate = item.create_date;
          if(!isExpectedDateSame) {
            createDate = __expectedDate; // 변경된 개선예정일이 있을 때 생성일 변경
          }

          // 데이터 가공
          return {
            improve_id: item.improve_id,
            improve_plan: item.improve_plan,
            improve_after_risk: item.improve_after_risk,
            emp_id: item.emp_id,
            expected_date: isExpectedDateSame ? expectedDate : __expectedDate,
            comp_date: isCompDateSame ? __compDate : compDate,
            improve_before: url1,
            improve_after: url2,
            improve_status: 20, // 20: 개선완료
            create_date: createDate,
            update_date: updateDate,
          };
        })
      );
      
      // 데이터베이스 업데이트
      const result = await putData.mutateAsync({ updateData });
      if(result.status === 200) {
        openDataAlert(`${updateData.length}건의 개선대책 수정 내용이 저장되었습니다.`);
        await getData.refetch?.();
        clearSelection();
      }
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
    }
  }

  // 감소대책 실행 결과 현황 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });

  // DB 업데이트 (삭제 데이터)
  const deleteEditData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = getSelectedRows().map((row) => row.original);
      // 선택된 행(row)의 id 가져오기
      const selectedIds = selectedRows.map((row) => row.improve_id);

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteParam: 'improve_id',
        param: selectedIds,
      });

      if(result.status === 200) {
        openDataAlert(message.deleteSuccess);
        await getData.refetch?.();
      }

      // 선택 상태 초기화
      clearSelection();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
    }
  };
  
  //////////////////////////////////
  // 개선전.후 사진 관련 변수 및 함수
  //////////////////////////////////

  // 개선전 사진 등록
  const addBeforeImage = (image) =>{
    //console.log(image);
    if(!image) {
      openAlarmMessage(
        '안내',
        message.retryImageError,
      );
      return;
    }

    // 편집된 데이터 변경
    const selectedData = {...showImproveImage.rowData};
    selectedData.improve_before_image = image.imageFile; // 개선전 사진 파일 
    selectedData.improve_before = image.previewUrl; // 개선전 사진 미리보기 URL
    selectedData.__imageStatus = ImageRenderState.NONE; // 이미지 상태 none

    // TempGetData 변경
    const tempData = tempGetData.map((item) => {
      return item.__index === selectedData.__index ? selectedData : item; 
    });

    handleSetTempGetData(tempData);
    closeShowImproveImage(); // 모달 닫기
  };

  // 개선후 사진 등록
  const addAfterImage = (image) =>{
    //console.log(image);
    if(!image) {
      openAlarmMessage(
        '안내',
        message.retryImageError,
      );
      return;
    }

    // 편집된 데이터 변경
    const selectedData = {...showImproveImage.rowData};
    selectedData.improve_after_image = image.imageFile; // 개선후 사진 파일 
    selectedData.improve_after = image.previewUrl; // 개선후 사진 미리보기 URL
    selectedData.__imageStatus = ImageRenderState.NONE; // 이미지 상태 none

    // TempGetData 변경
    const tempData = tempGetData.map((item) => {
      return item.__index === selectedData.__index ? selectedData : item; 
    });

    handleSetTempGetData(tempData);
    closeShowImproveImage(); // 모달 닫기
  };

  /////////////////////////////
  // 담당자 등록의 변수 및 함수
  /////////////////////////////

  // 담당자 등록
  const addEmployee = (rowData) => {
    //console.log(rowData);

    // 편집된 데이터 변경
    const selectedData = {...searchEmployee.rowData};
    selectedData.__hasSearchEmployee = false; // 담당자 검색 모달창 변수 초기화
    selectedData.emp_id = rowData.emp_id; // 직원 ID 초기화
    selectedData.emp_name = rowData.emp_name; // 직원 이름 초기화

    // TempGetData 변경
    const tempData = tempGetData.map(item => {
      return item.__index === selectedData.__index ? selectedData : item
    });

    handleSetTempGetData(tempData);
    closeSearchEmployee(); // 모달 닫기
  }

  ////////////////////////
  // Helper Functions
  ////////////////////////

  // 데이터 빈칸 체크
  const checkData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.comp_date || !item.improve_after
    );
    return hasValidData;
  };
  // 데이터 빈칸 체크 - 관리자 모드
  const checkDataByAdmin = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    for(const item of tempData) {
      const expectedDate = dayjs(item.__expectedDate).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환
      const compDate = dayjs(item.comp_date).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환

      const hasValidData = !item.improve_before || !item.improve_plan || !item.improve_after
        || !item.improve_after_risk || !expectedDate || !compDate || !item.emp_id;

      if(hasValidData) {
        return true;
      }
    }
    return false;
  };

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        {renderSummaryPanel()}
        {/** 개선대책 결과 현황 테이블 */}
        {!getData.isLoading && !(tempGetData || []).length ? (
          <EmptyState
            icon={<VerifiedIcon />}
            title="개선 완료 처리 대상이 없습니다"
            description="개선대책 수립 화면에서 대책을 등록하면 개선 후 사진과 완료일을 입력할 대상이 이곳에 표시됩니다."
          />
        ) : (
        <NewTableTemplate 
          ref={tableRef}
          key={ showAdminMode ? 'admin_table' : 'user_table' } // 관리자 모드 전환 시 테이블 리렌더링을 위해 key값 설정
          columns={ showAdminMode ? strategyResultColumns_admin : strategyResultColumns}
          data={tempGetData || []}
          topToolbarContent={customTableTopToolbar}
          renderRowActionHeader={completeEditAllConfirm}
          renderRowActionContent={completeEditConfirm}
          renderRowActionCustomOptions={changeRowData}
          editDisplayMode={"row"}
          enableCustomGlobalFilter={true}
          enabledSelectionRow={showAdminMode}
          enabledSelectionAllRow={showAdminMode}
          templateSX ={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "0.375rem", 
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
          }}
        />
        )}
        {/* 개선전 사진 등록 모달 */}
        {showImproveImage.state === ImageRenderState.BEFORE && (
          <RegisterBeforeImage 
            isOpen={showImproveImage.isOpen}
            onConfirm={addBeforeImage} 
            onCancel={closeShowImproveImage} 
          />
        )}
        {/* 개선후 사진 등록 모달 */}
        {showImproveImage.state === ImageRenderState.AFTER && (   
          <RegisterAfterImage 
            isOpen={showImproveImage.isOpen}
            onConfirm={addAfterImage}
            onCancel={closeShowImproveImage}
          />
        )}
        {/* 개선전 사진 확대 모달 */}
        {showImproveImage.state === ImageRenderState.EXPAND_BEFORE && (
          <ExpandBeforeImage 
            isOpen={showImproveImage.isOpen}
            imageURL={showImproveImage.rowData.improve_before}
            onConfirm={closeShowImproveImage}
          />
        )}
        {/* 개선후 사진 확대 모달 */}
        {showImproveImage.state === ImageRenderState.EXPAND_AFTER && (
          <ExpandAfterImage 
            isOpen={showImproveImage.isOpen}
            imageURL={showImproveImage.rowData.improve_after}
            onConfirm={closeShowImproveImage}
          />
        )}
        {/* 담당자 검색 모달 */}
        <SearchEmployee 
          isOpen={searchEmployee.isOpen} 
          onConfirm={addEmployee} 
          onCancel={closeSearchEmployee} 
        />
        {/* 삭제 메시지 */}
        <ConfirmDialog  
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={deleteEditData}
          onCancel={closeDeleteModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert 
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
        {/* 기타 메시지 박스 알람 */}
        <ConfirmDialogSingle  
          open={messageAlarm.isOpen}
          title={messageAlarm.title}
          message={messageAlarm.message}
          onCancel={closeAlarmMessage}
        />
      </TableContainer>
    );
  }
    
  // StrategyResult 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

// 개선전 사진 등록 모달
const RegisterBeforeImage = ({ isOpen, onConfirm, onCancel }) => {
  // 사진 등록 모달 참조
  const imageRef = useRef(null);

  return (
    <ModalTemplate 
      isOpen={isOpen}
      handleConfirm={()=> {
        const image = imageRef.current.getImage();
        onConfirm(image);
      }}
      handleCancel={onCancel}
      confirmText="등록"
      cancelText="취소"
      title="개선전 사진 등록"
      containerSX={{ width: 'auto', height: 'auto' }}
    >
      <ImageUploader ref={imageRef} />
    </ModalTemplate >
  );
};
// 개선후 사진 등록 모달
const RegisterAfterImage = ({ isOpen, onConfirm, onCancel }) => {
  // 사진 등록 모달 참조
  const imageRef = useRef(null);

  return (
    <ModalTemplate 
      isOpen={isOpen}
      handleConfirm={() => {
        const image = imageRef.current.getImage();
        onConfirm(image);
      }}
      handleCancel={onCancel}
      confirmText="등록"
      cancelText="취소"
      title="개선후 사진 등록"
      containerSX={{ width: 'auto', height: 'auto' }}
    >
      <ImageUploader ref={imageRef} />
    </ModalTemplate >
  );
};
// 개선전 사진 확대 모달
const ExpandBeforeImage = ({ isOpen, onConfirm, imageURL }) => {
  return(
    <ModalTemplate 
      isOpen={isOpen}
      handleConfirm={onConfirm}
      handleCancel={onConfirm}
      confirmText="확인"
      title="개선전 사진"
      enableSingleActionButton={true}
      contentSX={{
        margin: "12px 0px",
        //width: '25vw',
        //height: '25vh',
        width: 'auto',
        height: 'auto',
        border: `2px dashed ${COLORS.BORDER_DASH}`,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      <img 
        src={imageURL || null}
        // src={tempSelectedRowsData.current.improve_before || null}
        alt="Preview"
        style={{
          width: '100%',
          height: '100%',
          //objectFit: 'cover', // 또는 contain
          display: 'block',
          //borderRadius: 'inherit', // Box의 borderRadius 따라감
        }}
      />
    </ModalTemplate >
  );
};
// 개선후 사진 확대 모달
const ExpandAfterImage = ({ isOpen, onConfirm, imageURL }) => {
  return (
    <ModalTemplate 
      isOpen={isOpen}
      handleConfirm={onConfirm}
      handleCancel={onConfirm}
      confirmText="확인"
      title="개선후 사진"
      enableSingleActionButton={true}
      contentSX={{
        margin: "12px 0px",
        // width: '25vw',
        // height: '25vh',
        width: 'auto',
        height: 'auto',
        border: `2px dashed ${COLORS.BORDER_DASH}`,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      <img 
        src={imageURL || null}
        // src={tempSelectedRowsData.current.improve_after || null}
        alt="Preview"
        style={{
          width: '100%',
          height: '100%',
          //objectFit: 'cover', // 또는 contain
          display: 'block',
          //borderRadius: 'inherit', // Box의 borderRadius 따라감
        }}
      />
    </ModalTemplate >
  );
};

// 담당자 검색 모달
const SearchEmployee = ({ isOpen, onConfirm, onCancel }) =>{
  const { group_code, project_code } = useParams();
  const theme = useTheme();
  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 직원 조회 요청
  const getEmployeeData = useGetTableData(
    "request",
    "/getStandardEmployee",
    group_code,
    project_code,
  );

  // 메시지 박스의 알람 메시지
  const [messageAlarm, setMessageAlarm] = useState({
      isOpen: false,
      title: '',
      message: '',
  });
  const openAlarmMessage = (title='', message='',) => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: true,
      title: title,
      message: message,
    });
  };
  const closeAlarmMessage = () => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: false,
      title: '',
      message: '',
    });
  };

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const customTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* TODO: */}
      </Box>
      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
      
          <Collapse in={showSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setGlobalFilter('');
                            table.setGlobalFilter('');
                            setShowSearch(false); // 닫기
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
              }}
            />
          </Collapse>
        </Box>
      </Box>
    </Box>
  );

  return (
    <ModalTemplate 
      isOpen={isOpen}
      handleConfirm={()=> {
        if(getSelectedRows().length <= 0) {
          openAlarmMessage(
            '안내',
            '담당자를 선택해주세요.'
          );
          return;
        }
        if(getSelectedRows().length > 1) {
          openAlarmMessage(
            '안내',
            '담당자는 한 명만 지정할 수 있습니다.'
          );
          clearSelection();
          return;
        }
        onConfirm(getSelectedRows()[0].original);
        //handleEmployeeSelectedReset();
      }}
      handleCancel={onCancel}
      confirmText="등록"
      cancelText="취소"
      title="담당자 등록"
      titleSX={{ borderBottom: 'none' }}
      //contentSX={{ padding: "0px 0px 0px 0px", }}
      containerSX={{ width: '60%', height: '80%' }}
    >
      <NewTableTemplate 
        ref={tableRef}
        columns={strategyEmpSearchColumns}
        data={getEmployeeData.data || []}
        topToolbarContent={customTableTopToolbar}
        enableCustomGlobalFilter={true}
        enabledSelectionAllRow={false}
        templateSX={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "0.375rem", 
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
        }}
      />
      {/* 기타 메시지 박스 알람 */}
      <ConfirmDialogSingle  
        open={messageAlarm.isOpen}
        title={messageAlarm.title}
        message={messageAlarm.message}
        onCancel={closeAlarmMessage}
      />
    </ModalTemplate >
  );
};
